'use client'
import { memo, useMemo, useState, useEffect } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { motion } from 'motion/react'
import type { TypeLang } from '@/schema'
import { mq, designGrid, vw, getTransition, getButtonUnderline, getP24, getP18 } from '@/styles'
import { WhereToBuyFilter } from './WhereToBuyFilter'

type TStoreItem = {
  name: string
  street: string
  zip_code: string
  city: string
  phone: string
  website: string
  country: string
}

type TStoreMeta = {
  countries: { name: string; value: string }[]
}

type TStoreEntry = {
  data: TStoreItem[]
  meta: TStoreMeta
}

type TStores = Record<string, TStoreEntry>

type ISectionWhereToBuy = {
  stores: TStores
}

const TRANSLATIONS = {
  'en': {
    'country': 'Country',
    'city': 'City',
    'distributors': 'Distributors',
    'online': 'Online'
  },
  'es': {
    'country': 'País',
    'city': 'Ciudad',
    'distributors': 'Distribuidores',
    'online': 'En línea'
  },
  'fr': {
    'country': 'Pays',
    'city': 'Cité',
    'distributors': 'Distributeurs',
    'online': 'En ligne'
  },
  'it': {
    'country': 'Nazione',
    'city': 'Cittá',
    'distributors': 'Distributori',
    'online': 'Online'
  }
}

const getVariants = (variants, setVariant, selectedVariant, locale) => variants.map((variant, idx) => {
  const name = variant === 'distributors' || variant === 'online' ? TRANSLATIONS[locale][variant] : variant
  return (
    <li key={idx} className={idx === selectedVariant ? 'active' : ''}>
      <button onClick={() => setVariant(idx)}>{name}</button>
    </li>
  )
})

const getVariantInfos = (infos) => infos.map((info, idx) => {
  const { name, street, zip_code, city, phone, website, country } = info

  return (
    <li key={idx}>
      <p>{name}</p>
      <p>{street}</p>
      <p>{zip_code} {city}</p>
      <p>{country}</p>
      <p>
        <button><a href={`tel:${phone}`}>{phone}<span></span></a></button>
      </p>
      <p>
        <button><a href={website.includes('http') ? website : `https://${website}`} rel='noreferrer' target='_blank' title={website}>{website}<span></span></a></button>
      </p>
    </li>
  )
})

const isNotPlaceholder = (value, keyName) => {
  const languages = Object.keys(TRANSLATIONS)
  let notPlaceholder = true
  for(const locale of languages) {
    if(TRANSLATIONS[locale][keyName] === value) notPlaceholder = false
  }

  return notPlaceholder
}

export const SectionWhereToBuy = memo<ISectionWhereToBuy>(({ stores }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const variantKeys = Object.keys(stores)
  const [selectedVariant, setVariant] = useState(0)
  const [selectedCountry, setCountry] = useState(lang ? TRANSLATIONS[lang].country : 'Country')
  const [selectedCity, setCity] = useState(lang ? TRANSLATIONS[lang].city : 'City')
  const storesInfo = stores[variantKeys[selectedVariant]].data
  const [filteredVariants, setFilteredVariants] = useState(storesInfo)
  const Variants = useMemo(() => getVariants(variantKeys, setVariant, selectedVariant, lang), [variantKeys, selectedVariant, lang])
  const VariantInfo = useMemo(() => getVariantInfos(filteredVariants), [filteredVariants])

  useEffect(() => {
		let incomingVariants = storesInfo
    if(isNotPlaceholder(selectedCountry, 'country') && isNotPlaceholder(selectedCity, 'city')) {
      const filteredByCountry = storesInfo.filter(distributor => distributor.country === selectedCountry)
      const filteredByCity = filteredByCountry.filter(distributor => distributor.city === selectedCity)
			incomingVariants = filteredByCity
    } else if (isNotPlaceholder(selectedCity, 'city')) {
      const filteredByCity = storesInfo.filter(distributor => distributor.city === selectedCity)
			incomingVariants = filteredByCity
    } else if(isNotPlaceholder(selectedCountry, 'country')) {
      const filteredByCountry = storesInfo.filter(distributor => distributor.country === selectedCountry)
			incomingVariants = filteredByCountry
    }
		queueMicrotask(() => setFilteredVariants(incomingVariants))
  }, [selectedCity, selectedCountry, selectedVariant, storesInfo])

  useEffect(() => {
    queueMicrotask(() => setCountry(lang ? TRANSLATIONS[lang].country : 'Country'))
    queueMicrotask(() => setCity(lang && TRANSLATIONS[lang] ? TRANSLATIONS[lang].city : 'City'))	
  }, [lang, selectedVariant])

  return (
    <Section>
      <ul className='variants'>
        {Variants}
      </ul>
      <WhereToBuyFilter data={stores[variantKeys[selectedVariant]].meta} key={selectedVariant} setCountry={setCountry} selectedCountry={selectedCountry} setCity={setCity} selectedCity={selectedCity} filterResults={filteredVariants.length} online={selectedVariant > 0}/>
      <motion.ul className='variant-info' key={selectedCity + selectedCountry + selectedVariant} variants={imageVariants} initial='initial' exit='exit' animate='animate'>
        {(selectedCountry === 'Country' && selectedCity === 'City') ? <li></li> : VariantInfo}
      </motion.ul>
    </Section>
  )
})


const Section = styled.section`
  ${designGrid({})}
  margin-bottom: ${vw(100, 'mobile')};
  padding-top: ${vw(50, 'mobile')};

  ${mq.greaterThan('tablet')} {
    grid-row-gap: ${vw(90, 'desktop')};
    padding-top: ${vw(140, 'desktop')};
    margin-bottom: ${vw(130, 'desktop')};
  }

  li, button, a, p, div {
    ${getP18()}
  }

  button {
    ${getButtonUnderline()}
  }

  .variants {
    background-color: var(--color-background);
    grid-area: 1 / 1 / span 1 / span 6;
    padding: ${vw(19, 'mobile')} 0 ${vw(33, 'mobile')};
    position: sticky;
    top: ${vw(50, 'mobile')};
    z-index: 10;

    ${mq.greaterThan('tablet')}{
      grid-area: 1 / 1 / span 1 / span 3;
      margin: 0 0 0 ${vw(40, 'desktop')};
      padding: 0;
      position: static;
    }

    li {
      display: inline-block;
      margin-right: ${vw(20, 'mobile')};
      opacity: .4;
      transition: opacity 300ms var(--transition-ease);

      ${mq.greaterThan('tablet')}{
        display: block;
        margin-right: 0;
      }

      &.active,
      &:hover {
        opacity: 1;
      }

      button {
        ${getP24()}
      }
    }

    + .variant-info {
      margin-top: ${vw(11, 'mobile')};

      ${mq.greaterThan('tablet')}{
        margin-top: 0;
      }
    }
  }

  .variant-info {
    grid-area: 3 / 1 / span 1 / span 6;
    margin: ${vw(30, 'mobile')} 0 -${vw(20, 'mobile')};
    opacity: 0;

    ${mq.greaterThan('tablet')}{
      column-count: 2;
      grid-area: 2 / 5 / span 1 / span 6;
      margin: 0 ${vw(21, 'desktop')} -${vw(30, 'desktop')};
    }

    li {
      padding-bottom: ${vw(20, 'mobile')};

      ${mq.greaterThan('tablet')}{
        break-inside: avoid-column;
        padding-bottom: ${vw(30, 'desktop')};
        position: relative;
      }

      p:not(:first-of-type) {
        opacity: 0.4;
      }

      button, a {
        color: var(--color-text);
      }
    }
  }
`

const imageVariants = {
  'initial': {
    opacity: 0,
    transition: {
      ...getTransition(),
    }
  },
  'animate': {
    opacity: 1,
    transition: {
      ...getTransition(),
    }
  },
  'exit': {
    opacity: 0,
    transition: {
      ...getTransition(),
    }
  }
}